import type { RefObject } from "react";
import { createPortal } from "react-dom";

import type { BillingInterval } from "store/slices/reduxApi/billing";

import { ReactComponent as SparklesIcon } from "../../assets/icons/common/sparkles.svg";
import { ManagePlanUsageBar } from "./ManagePlanUsageBar";
import type { AiCreditPackOption } from "./types";

type ManagePlanCreditPanelProps = {
  billingInterval: BillingInterval;
  availableCreditPackOptions: AiCreditPackOption[];
  currentPlanCredits: number;
  currentUsageCredits: number;
  topupCreditsPurchased?: number;
  topupCreditsUsed?: number;
  selectedCredits: number | null;
  selectedPack: AiCreditPackOption | null;
  isDropdownOpen: boolean;
  dropdownPosition: { top: number; left: number; width: number } | null;
  triggerRef: RefObject<HTMLButtonElement>;
  dropdownRef: RefObject<HTMLDivElement>;
  disabled?: boolean;
  onToggleDropdown: () => void;
  onSelectCredits: (credits: number | null) => void;
  /**
   * When provided, renders a "Refill credits" primary button on the Current
   * plan row. Clicking it flips the parent Manage Plan modal into Top up
   * mode (no stacked modal — same modal frame, swapped body). Omitted when
   * the org is ineligible for one-time top ups (no payment method, plan
   * ineligible, etc.) — caller decides eligibility.
   */
  onBuyCredits?: () => void;
};

function formatUsd(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatCreditPackLabel(
  credits: number,
  priceUsd: number,
  billingInterval: BillingInterval,
) {
  const intervalLabel = billingInterval === "annual" ? "annually" : "monthly";
  return `${credits.toLocaleString()} credits/month (${formatUsd(priceUsd)} ${intervalLabel})`;
}

export function ManagePlanCreditPanel({
  billingInterval,
  availableCreditPackOptions,
  currentPlanCredits,
  currentUsageCredits,
  topupCreditsPurchased = 0,
  topupCreditsUsed = 0,
  selectedCredits,
  selectedPack,
  isDropdownOpen,
  dropdownPosition,
  triggerRef,
  dropdownRef,
  disabled = false,
  onToggleDropdown,
  onSelectCredits,
  onBuyCredits,
}: ManagePlanCreditPanelProps) {
  const selectedPrice = selectedPack?.priceUsd ?? 0;

  return (
    <div
      data-test="manage-credit-panel"
      className="flex flex-col overflow-hidden rounded-lg bg-white shadow-[0_0_0_1px_rgba(31,38,51,0.12)]"
    >
      <div className="flex items-center gap-2 border-b border-[#e8eaed] bg-[#f9fafb] px-4 py-3">
        <SparklesIcon className="h-4 w-4" />
        <span className="text-xs font-medium leading-4 text-[#1f2633]">
          Additional AI Credits
        </span>
      </div>
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs leading-4 text-[#6c7689]">
              Current plan
            </span>
            <span className="text-[13px] font-medium leading-5 text-[#1f2633]">
              {currentPlanCredits.toLocaleString()} Org Credits
            </span>
          </div>
          {onBuyCredits ? (
            <button
              type="button"
              data-test="manage-plan-buy-credits"
              onClick={disabled ? undefined : onBuyCredits}
              disabled={disabled}
              className="inline-flex h-8 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-black px-3 text-xs font-medium !text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-black/90 disabled:cursor-not-allowed disabled:bg-[#454d5f] disabled:opacity-60"
            >
              Refill credits
            </button>
          ) : null}
        </div>

        <ManagePlanUsageBar
          used={currentUsageCredits}
          total={
            selectedPack
              ? selectedPack.credits + topupCreditsPurchased
              : currentPlanCredits + topupCreditsPurchased
          }
          topupPurchased={topupCreditsPurchased}
          topupUsed={topupCreditsUsed}
        />

        <div className="h-0 border-b border-[#e8eaed]" />

        <div className="flex flex-col gap-3">
          <span className="text-xs leading-4 text-[#6c7689]">
            {currentPlanCredits > 0
              ? "Adjust Org Credit Pack (Recurring Monthly)"
              : "Add org-wide credits"}
          </span>
          <button
            ref={triggerRef}
            type="button"
            data-test="credit-pack-trigger"
            onClick={disabled ? undefined : onToggleDropdown}
            disabled={disabled}
            className="flex h-8 w-full cursor-pointer items-center gap-1 rounded-[6px] border-0 bg-white px-3 py-2 text-left shadow-[0_0_0_1px_rgba(31,38,51,0.12)] transition hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span
              className={`flex-1 truncate text-xs leading-4 ${selectedPack || selectedCredits === 0 ? "text-[#454d5f]" : "text-[#a4aab7]"}`}
            >
              {selectedPack
                ? formatCreditPackLabel(
                    selectedPack.credits,
                    selectedPrice,
                    billingInterval,
                  )
                : selectedCredits === 0
                  ? "No additional credits"
                  : "Select AI Credit Pack"}
            </span>
            <svg
              className={`h-4 w-4 shrink-0 text-[#6c7689] transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 6l4 4 4-4" />
            </svg>
          </button>
          {isDropdownOpen && dropdownPosition
            ? createPortal(
                <div
                  ref={dropdownRef}
                  data-test="credit-pack-dropdown"
                  className="max-h-[320px] overflow-x-hidden overflow-y-auto rounded-lg border border-[#e8eaed] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.12)]"
                  style={{
                    position: "fixed",
                    top: dropdownPosition.top,
                    left: dropdownPosition.left + dropdownPosition.width - 355,
                    width: 355,
                    zIndex: 2000,
                  }}
                >
                  {currentPlanCredits === 0 && (
                    <button
                      type="button"
                      onClick={() => onSelectCredits(0)}
                      className={`flex w-full items-center justify-between border-0 px-3 py-2.5 text-left text-xs transition ${selectedCredits === 0 ? "cursor-pointer bg-[#f9fafb] text-[#1f2633]" : "cursor-pointer bg-white text-[#1f2633] hover:bg-[#f9fafb]"}`}
                    >
                      <span>No additional credits</span>
                      <span
                        className={`text-sm leading-none ${selectedCredits === 0 ? "text-[#1f2633]" : "text-transparent"}`}
                      >
                        ✓
                      </span>
                    </button>
                  )}
                  {availableCreditPackOptions.map((option) => {
                    const isSelected = option.credits === selectedCredits;
                    const isCurrent = option.credits === currentPlanCredits;

                    return (
                      <button
                        key={option.id}
                        type="button"
                        data-test={`credit-pack-option-${option.credits}`}
                        onClick={() => onSelectCredits(option.credits)}
                        className={`flex w-full items-center justify-between border-0 px-3 py-2.5 text-left text-xs transition ${isSelected || isCurrent ? "cursor-pointer bg-[#f9fafb] text-[#1f2633]" : "cursor-pointer bg-white text-[#1f2633] hover:bg-[#f9fafb]"}`}
                      >
                        <span>
                          {formatCreditPackLabel(
                            option.credits,
                            option.priceUsd,
                            billingInterval,
                          )}
                          {isCurrent ? (
                            <span className="ml-2 rounded-full bg-[#e8eaed] px-2 py-0.5 text-[11px] font-medium text-[#6c7689]">
                              Current
                            </span>
                          ) : null}
                        </span>
                        <span
                          className={`text-sm leading-none ${isSelected ? "text-[#1f2633]" : "text-transparent"}`}
                        >
                          ✓
                        </span>
                      </button>
                    );
                  })}
                </div>,
                document.body,
              )
            : null}
        </div>
      </div>
    </div>
  );
}
